/**
 * FolderContentDialog Component
 * Dialog zum Anzeigen und Verwalten des Ordner-Inhalts
 * Apps können per Klick geöffnet oder aus dem Ordner entfernt werden
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Trash2, Edit2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { APPS } from '@/data/apps'
import type { DesktopFolder } from '@/services/folder-service'

interface FolderContentDialogProps {
  isOpen: boolean
  folderId: string
  folder: DesktopFolder | null
  onClose: () => void
  onRename: (newName: string) => void
  onRemoveApp: (appId: string) => void
  onDeleteFolder: () => void
  onOpenApp: (appId: string) => void
}

export function FolderContentDialog({
  isOpen,
  folderId: _folderId,
  folder,
  onClose,
  onRename,
  onRemoveApp,
  onDeleteFolder,
  onOpenApp,
}: FolderContentDialogProps) {
  void _folderId // Used in interface, kept for potential future use
  const { t } = useTranslation()
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')

  if (!isOpen || !folder) return null

  const handleStartEdit = () => {
    setEditedName(folder.name)
    setIsEditingName(true)
  }

  const handleSaveEdit = () => {
    if (editedName.trim()) {
      onRename(editedName.trim())
    }
    setIsEditingName(false)
  }

  const handleCancelEdit = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[15000]"
      onClick={onClose}
    >
      <div
        className="bg-[var(--window-background)] rounded-2xl border border-[var(--window-border-color)] shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--window-border-color)]">
          <div className="flex items-center gap-3 flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-xl font-semibold"
                  autoFocus
                  maxLength={50}
                />
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  aria-label={t('common.save')}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                  aria-label={t('common.cancel')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-[var(--text-color)] flex-1">
                  {folder.name}
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleStartEdit}
                  title={t('widgets.desktop.renameFolder')}
                  aria-label={t('widgets.desktop.renameFolder')}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onDeleteFolder()
                    onClose()
                  }}
                  title={t('widgets.desktop.deleteFolder')}
                  aria-label={t('widgets.desktop.deleteFolder')}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content - App Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {folder.appIds.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-[var(--text-secondary)]">
              {t('widgets.desktop.emptyFolder')}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-6">
              {folder.appIds.map((appId) => {
                const app = APPS.find((a) => a.id === appId)
                if (!app) return null

                const appName = t(`apps.${appId}`)
                const Icon = app.Icon

                return (
                  <div
                    key={appId}
                    className="flex flex-col items-center justify-center p-4 rounded-xl hover:bg-[var(--window-background)]/60 transition-all cursor-pointer group"
                    onClick={() => onOpenApp(appId)}
                  >
                    <div className="relative">
                      <div
                        className="w-16 h-16 flex items-center justify-center rounded-2xl mb-3 transition-transform group-hover:scale-110 bg-[var(--surface-secondary)]"
                      >
                        <Icon
                          className="h-8 w-8 text-[hsl(var(--accent))]"
                        />
                      </div>

                      {/* Entfernen-Button (erscheint bei Hover) */}
                      <button
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          onRemoveApp(appId)
                        }}
                        aria-label={`${appName} entfernen`}
                        title={`${appName} aus Ordner entfernen`}
                      >
                        <X className="w-3 h-3 text-white" strokeWidth={3} />
                      </button>
                    </div>

                    <div
                      className="text-center text-sm font-medium text-[var(--text-color)] truncate w-full"
                      title={appName}
                    >
                      {appName}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
